/**
 * PlayerCharacter — persistent player stats that survive across battles.
 *
 * Created once per run, stored inside RunState.
 * Extends GameCharacter for shared combat stats.
 */
import { GameCharacter } from "./GameCharacter";
import { xpForLevel, MAX_LEVEL } from "./progression/XPTable";
import { ShapeInventory } from "./shapes";
import { GridMask } from "../scenes/battle/grid/GridMask";
import { SkillRegistry } from "./skills";
import { Inventory } from "../items";

export class PlayerCharacter extends GameCharacter {
  gold: number;
  level: number;
  baseAttack: number;
  maxAp: number;
  shapes: ShapeInventory;
  gridMask: GridMask;
  /** Typed skill and perk storage. */
  readonly skills: SkillRegistry;
  /** Consumable items. */
  readonly items: Inventory;

  /** Current XP within the current level. */
  xp: number = 0;
  /** XP needed to reach next level. */
  xpToNext: number;

  constructor(maxHp: number = 10, gold: number = 0, level: number = 1) {
    super("You", maxHp, 1, 0);
    this.gold = gold;
    this.level = level;
    this.baseAttack = 1;
    this.maxAp = 2;
    this.shapes = new ShapeInventory();
    this.gridMask = GridMask.base(4);
    this.skills = new SkillRegistry();
    this.items = new Inventory();
    this.items.add("shuffle", 3);
    this.xpToNext = xpForLevel(level);
  }

  /** Player attack uses baseAttack (can be overridden by ModifierBag). */
  override get attack(): number {
    return this.baseAttack;
  }

  override set attack(value: number) {
    this.baseAttack = value;
  }

  /** XP progress as 0–1 ratio for bar display. */
  get xpProgress(): number {
    if (this.level >= MAX_LEVEL) return 1;
    return this.xpToNext > 0 ? this.xp / this.xpToNext : 0;
  }

  /**
   * Add XP. Returns the number of levels gained (0 if none).
   * XP rolls over into the next level if it exceeds the threshold.
   */
  addXp(amount: number): number {
    if (this.level >= MAX_LEVEL) return 0;

    this.xp += amount;
    let levelsGained = 0;

    while (this.xp >= this.xpToNext && this.level < MAX_LEVEL) {
      this.xp -= this.xpToNext;
      this.level++;
      levelsGained++;
      this.xpToNext = xpForLevel(this.level);
    }

    // Clamp at max level
    if (this.level >= MAX_LEVEL) {
      this.xp = 0;
      this.xpToNext = 0;
    }

    return levelsGained;
  }

  addGold(amount: number): void {
    this.gold += amount;
  }

  spendGold(amount: number): boolean {
    if (this.gold < amount) return false;
    this.gold -= amount;
    return true;
  }
}
